import { UserManager, WebStorageStateStore, User } from 'oidc-client';
// Add type declaration for window.ENV
import { oktaDetails } from '@/lib/config';
import { clearLocalAuth, getLocalAuthUser, isLocalAuthenticated } from '@/services/localAuth';
declare global {
    interface Window {
        ENV: {
            OKTA_ISSUER: string;
            OKTA_CLIENT_ID: string;
            OKTA_REDIRECT_URI: string;
            OKTA_POST_LOGOUT_REDIRECT_URI: string;
            API_URL?: string; // Add optional API_URL to match the expected type
        }
    }
}

// Use window.ENV instead of imported config
// const oktaConfig = {
//     authority: window.ENV?.OKTA_ISSUER,
//     client_id: window.ENV?.OKTA_CLIENT_ID,
//     redirect_uri: window.ENV?.OKTA_REDIRECT_URI,
//     post_logout_redirect_uri: window.ENV?.OKTA_POST_LOGOUT_REDIRECT_URI,
//     response_type: 'code',
//     scope: 'openid profile email',
//     userStore: new WebStorageStateStore({ store: window?.localStorage })
// };
const oktaConfig = {
    authority: oktaDetails.authority,
    client_id: oktaDetails.client_id,
    redirect_uri: oktaDetails.redirect_uri,
    post_logout_redirect_uri: oktaDetails.post_logout_redirect_uri,
    response_type: 'code',
    // Add offline_access scope to get refresh tokens
    scope: 'openid profile email pepappdcsroles offline_access',
    // Remove client_secret from browser configuration
    // client_secret: oktaDetails.client_secret,
    // Set token lifetime, refresh before expiration
    accessTokenExpiringNotificationTime: 300, // 5 minutes before expiration
    automaticSilentRenew: true, // Enable automatic token renewal
    userStore: new WebStorageStateStore({ store: window?.localStorage }),
    // Enable PKCE - required by Okta for browser-based flows
    usePkce: true
};

class OktaService {
    private userManager: UserManager;
    private refreshInterval: number | null = null;

    constructor() {
        this.userManager = new UserManager({ ...oktaConfig });

        // Set up event listeners with correct method names
        this.userManager.events.addAccessTokenExpiring(this.handleTokenExpiring);
        this.userManager.events.addAccessTokenExpired(this.handleTokenExpired);
        this.userManager.events.addSilentRenewError(this.handleSilentRenewError);
        this.userManager.events.addUserLoaded(this.handleUserLoaded);

        // Check for existing user and start refresh cycle if found
        this.initializeTokenRefresh();
    }

    // Initialize token refresh for existing sessions
    private initializeTokenRefresh = async (): Promise<void> => {
        try {
            const user = await this.userManager.getUser();
            if (user && !user.expired) {
                this.startTokenRefreshCycle();
            }
        } catch (error) {
            console.error('Error initializing token refresh:', error);
        }
    };

    // Handle token expiring event
    private handleTokenExpiring = (): void => {
        console.log('Access token is about to expire, attempting silent renewal');
    };

    // Handle token expired event
    private handleTokenExpired = (): void => {
        console.log('Access token has expired, attempting renewal');
        this.userManager.signinSilent()
            .catch(error => {
                console.error('Silent renew failed:', error);
                // Token refresh failed, but we'll let the normal flow handle redirects
                // rather than forcing a logout here
            });
    };

    // Handle user loaded event (called when tokens are refreshed successfully)
    private handleUserLoaded = (user: User): void => {
        console.log('Token successfully renewed for user:', user.profile.email || user.profile.sub);
    };

    // Handle silent renew errors
    private handleSilentRenewError = (error: Error): void => {
        console.error('Silent renew error:', error);
    };

    // Start the token refresh cycle (every 30 minutes)
    private startTokenRefreshCycle = (): void => {
        // Clear any existing interval
        this.stopTokenRefreshCycle();

        // Start a new cycle - refresh tokens every 30 minutes
        this.refreshInterval = window.setInterval(async () => {
            try {
                const user = await this.userManager.getUser();
                if (user && !user.expired) {
                    console.log('Refreshing token as scheduled');
                    await this.userManager.signinSilent();
                }
            } catch (error) {
                console.error('Scheduled token refresh failed:', error);
            }
        }, 30 * 60 * 1000); // 30 minutes

        console.log('Token refresh cycle started');
    };

    // Stop the token refresh cycle
    private stopTokenRefreshCycle = (): void => {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
            console.log('Token refresh cycle stopped');
        }
    };

    // Initiate login
    public login = async (): Promise<void> => {
        try {
            await this.userManager.signinRedirect();
        } catch (error) {
            console.error('Okta login error:', error);
            throw error;
        }
    };

    // Handle the callback from Okta
    public handleCallback = async (): Promise<User> => {
        try {
            const user = await this.userManager.signinRedirectCallback();
            // Start token refresh cycle after successful login
            this.startTokenRefreshCycle();
            return user;
        } catch (error) {
            console.error('Okta callback error:', error);
            throw error;
        }
    };

    // Logout from Okta
    public logout = async (redirectToLogin: boolean = true, redirectFirst: boolean = false): Promise<void> => {
        try {
            if (isLocalAuthenticated()) {
                clearLocalAuth();
                this.stopTokenRefreshCycle();
                return;
            }

            if (redirectFirst) {
                await this.userManager.signoutRedirect();

            } else {
                this.stopTokenRefreshCycle();

                // Get current user
                const user = await this.userManager.getUser();

                // Clear the user from local storage
                await this.userManager.removeUser();

                // If redirectToLogin is true, redirect to Okta logout
                if (redirectToLogin && user) {
                    await this.userManager.signoutRedirect();
                }
            }
            // Stop token refresh cycle

        } catch (error) {
            console.error('Okta logout error:', error);
            throw error;
        }
    };

    // Get the current user
    public getUser = async (): Promise<User | null> => {
        try {
            const localUser = getLocalAuthUser();
            if (localUser) {
                return {
                    access_token: localUser.access_token,
                    expired: false,
                    profile: {
                        email: localUser.email,
                        sub: localUser.gpid,
                        name: localUser.displayName,
                    },
                } as unknown as User;
            }

            return await this.userManager.getUser();
        } catch (error) {
            console.error('Get user error:', error);
            return null;
        }
    };

    // Check if the user is authenticated
    public isAuthenticated = async (): Promise<boolean> => {
        try {
            if (isLocalAuthenticated()) {
                return true;
            }

            const user = await this.userManager.getUser();
            return !!user && !user.expired;
        } catch (error) {
            console.error('Authentication check error:', error);
            return false;
        }
    };

    // Manually refresh the token if needed
    public refreshToken = async (): Promise<boolean> => {
        try {
            if (isLocalAuthenticated()) {
                return true;
            }

            await this.userManager.signinSilent();
            return true;
        } catch (error) {
            console.error('Manual token refresh failed:', error);
            return false;
        }
    };
}

// Create a singleton instance
const oktaService = new OktaService();
export default oktaService;
